import React from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import PageHeader from '../../components/account/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import Icon from '../../components/ui/Icon';
import Text from '../../components/ui/Text';
import { paymentIcon, paymentMethods } from '../../data/account';
import {
  colors,
  radii,
  screenPadding,
  shadows,
  spacing,
  useResponsive,
} from '../../theme';

/**
 * Cards, UPI and the wallet.
 *
 * ---------------------------------------------------------------------------
 * NOTHING HERE TOUCHES A PAYMENTS PROVIDER
 * ---------------------------------------------------------------------------
 * There is no gateway, no tokenisation and no wallet ledger. The list is seed
 * data - see `src/data/account.ts` - and "Add New" says so rather than opening
 * a card form that could only pretend to save.
 *
 * One thing worth keeping when this becomes real: no card number is stored,
 * only the brand and the last four. That is all a list needs, and it is the
 * only part a client is ever allowed to hold.
 */

type Props = {
  onBack: () => void;
  /** Raised when a control needs a provider that does not exist yet. */
  onUnavailable: () => void;
};

function PaymentMethodsScreen({ onBack, onUnavailable }: Props) {
  const resp = useResponsive();
  return (
    <View style={styles.root}>
      {/*
        No "+ Add New".

        It routed to `onUnavailable`, which for a signed-in customer is a
        literal no-op - the button looked live, absorbed the tap and did
        nothing at all. There is nowhere to add an instrument TO: the app holds
        no vault and Razorpay collects the card at checkout instead.
      */}
      <PageHeader title="Payment Methods" onBack={onBack} />

      {/*
        `flex: 1` on the scroller itself, not only on its content.

        Inside a flex column a ScrollView with no flex of its own sizes to its
        content and overflows the parent, so the foot of a long list is simply
        cut off - worst on whichever filter shows the most. Flexed, it takes the
        space left under the header and scrolls inside it.
      */}
      <ScrollView
        style={styles.scroller}
        /* Zero on a phone; on a tablet it holds the page to a readable
           column and centres it. See `theme/responsive.ts`. */
        contentContainerStyle={[styles.page, resp.page]}
        showsVerticalScrollIndicator={false}
      >
        {paymentMethods.length === 0 ? (
          /*
            What this page honestly is right now.

            It used to list five invented instruments - a UPI handle, three
            cards and a "₹450.00" wallet - every one of them pressable, and
            every press a no-op. Nothing was ever stored, so there was nothing
            to show and nothing to tap.
          */
          <EmptyState
            icon="creditCard"
            title="No saved payment methods"
            message="You choose how to pay when you book, and we never store your card details."
          />
        ) : null}

        {paymentMethods.map(method => (
          <Pressable
            key={method.id}
            accessibilityRole="button"
            accessibilityLabel={`${method.label}, ${method.detail}${
              method.isDefault ? ', default' : ''
            }`}
            onPress={onUnavailable}
            style={({ pressed }) => [
              styles.card,
              pressed ? styles.pressed : null,
            ]}
          >
            {/* A tinted tile rather than a brand logo: shipping Visa and
                Mastercard artwork means shipping their trademarks, and the
                icon set already carries a card glyph. */}
            <View style={styles.mark}>
              <Icon
                name={paymentIcon[method.kind]}
                size={19}
                color={colors.primary}
              />
            </View>

            <View style={styles.body}>
              <Text variant="subtitle">{method.label}</Text>
              <Text variant="caption" color={colors.inkMuted}>
                {method.detail}
              </Text>
              {method.note ? (
                <Text variant="caption" color={colors.inkMuted}>
                  {method.note}
                </Text>
              ) : null}
              {method.isDefault ? (
                <View style={styles.badge}>
                  <Text variant="caption" color={colors.primary}>
                    Default
                  </Text>
                </View>
              ) : null}
            </View>

            <Icon name="chevronRight" size={16} color={colors.borderStrong} />
          </Pressable>
        ))}

        {/* Last, and quiet. It is reassurance, not a claim to read first. */}
        <View style={styles.footnote}>
          <Icon name="shieldCheck" size={15} color={colors.success} />
          <Text variant="caption" color={colors.inkMuted}>
            100% Secure Payments
          </Text>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scroller: {
    flex: 1,
  },
  page: {
    /* Vertical only: the horizontal gutter comes from `resp.page` on the
       contentContainer, which also carries the tablet inset. `padding`
       here was silently overridden by it. */
    paddingVertical: screenPadding,
    gap: spacing.md,
    // Clears the floating tab bar, which overlays the bottom of the screen.
    paddingBottom: 120,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...shadows.sm,
  },
  mark: {
    width: 42,
    height: 42,
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  body: {
    flex: 1,
    gap: 2,
    alignItems: 'flex-start',
  },
  badge: {
    marginTop: 2,
    paddingHorizontal: spacing.sm,
    paddingVertical: 1,
    borderRadius: radii.pill,
    backgroundColor: colors.surfaceAccent,
  },
  footnote: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingTop: spacing.xl,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default PaymentMethodsScreen;
